import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, TrendingUp, BarChart3, PieChart, History, Wallet, HelpCircle, Menu, ChevronLeft } from 'lucide-react';
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}
interface SidebarProps {
  onNavigate?: (itemId: string) => void;
}
export function Sidebar({
  onNavigate
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(true);
  const navItems: NavItem[] = [{
    id: 'home',
    label: 'Home',
    icon: <Home size={20} data-id="element-1256" />,
    path: '/'
  }, {
    id: 'portfolio',
    label: 'Portfolio',
    icon: <Wallet size={20} data-id="element-1257" />,
    path: '/portfolio'
  }, {
    id: 'markets',
    label: 'Markets',
    icon: <BarChart3 size={20} data-id="element-1258" />,
    path: '/markets'
  }, {
    id: 'analytics',
    label: 'Analytics',
    icon: <PieChart size={20} data-id="element-1259" />,
    path: '/analytics'
  }, {
    id: 'trade',
    label: 'Trade',
    icon: <TrendingUp size={20} data-id="element-1260" />,
    path: '/trade'
  }, {
    id: 'history',
    label: 'History',
    icon: <History size={20} data-id="element-1261" />,
    path: '/history'
  }];
  const handleItemClick = (item: NavItem) => {
    navigate(item.path);
    onNavigate?.(item.id);
  };
  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };
  return <div className={`
        flex flex-col h-screen bg-background-primary border-r border-white/[0.04]
        transition-all duration-200
        ${isExpanded ? 'w-56' : 'w-14'}
      `} data-id="element-1262">
      {/* Header */}
      <div className="flex items-center justify-between p-3" data-id="element-1263">
        {isExpanded ? <>
            <div className="flex items-center gap-2" data-id="element-1264">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white" data-id="element-1265">
                <path fillRule="evenodd" clipRule="evenodd" d="M2 13.8332V18.1963C2 18.9998 2.63994 19.6499 3.42687 19.6499C4.21464 19.6499 4.85762 18.9998 4.85762 18.1963V13.8332C4.85762 13.0286 5.49424 12.3773 6.28532 12.3773C7.07364 12.3773 7.71302 13.0286 7.71302 13.8332V18.1963C7.71302 18.9998 8.35296 19.6499 9.14073 19.6499C9.93125 19.6499 10.5709 18.9998 10.5709 18.1963V13.8332C10.5709 13.0286 11.2103 12.3773 11.9983 12.3773C12.7897 12.3773 13.4296 13.0286 13.4296 13.8332V18.1963C13.4296 18.9998 14.0693 19.6499 14.8562 19.6499C15.644 19.6499 16.2839 18.9998 16.2839 18.1963V13.8332C16.2839 13.0286 16.9236 12.3773 17.7147 12.3773C18.5027 12.3773 19.1424 13.0286 19.1424 13.8332V18.1963C19.1424 18.9998 19.7823 19.6499 20.5723 19.6499C21.3603 19.6499 22 18.9998 22 18.1963V13.8332C22 8.20968 17.5218 3.6499 11.9983 3.6499C6.47514 3.6499 2 8.20968 2 13.8332Z" className="fill-current" data-id="element-1266" />
              </svg>
              <span className="text-sm text-white font-semibold tracking-tight" style={{
            letterSpacing: '-0.04em'
          }} data-id="element-1267">
                PRO
              </span>
            </div>
            <button onClick={() => setIsExpanded(false)} className="p-1 hover:bg-background-elevated rounded transition-colors" data-id="element-1268">
              <ChevronLeft size={16} data-id="element-1269" />
            </button>
          </> : <button onClick={() => setIsExpanded(true)} className="p-1 hover:bg-background-elevated rounded transition-colors mx-auto" data-id="element-1270">
            <Menu size={20} data-id="element-1271" />
          </button>}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 px-2 relative" data-id="element-1272">
        {navItems.map(item => {
        const active = isActive(item.path);
        return <button key={item.id} onClick={() => handleItemClick(item)} className={`
                w-full flex items-center gap-3 px-3 py-3 transition-all rounded-lg mb-1 relative
                ${active ? 'text-white' : 'text-text-secondary hover:bg-white/[0.06] hover:text-text-primary'}
                ${!isExpanded && 'justify-center'}
              `} title={!isExpanded ? item.label : undefined} data-id="element-1273">
              {/* Animated background for active item */}
              {active && <motion.div layoutId="activeNav" className="absolute inset-0 bg-brand-purple/15 rounded-lg" initial={false} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 35,
            mass: 0.8
          }} data-id="element-1274" />}

              {/* Icon - no animation */}
              <div className="relative z-10" data-id="element-1275">{item.icon}</div>

              {/* Label - no animation */}
              {isExpanded && <span className="text-sm font-medium relative z-10" data-id="element-1276">
                  {item.label}
                </span>}
            </button>;
      })}
      </nav>

      {/* Footer */}
      <div className="p-3" data-id="element-1277">
        <button className={`
            w-full flex items-center gap-3 px-3 py-2.5 text-text-secondary
            hover:text-text-primary hover:bg-background-elevated rounded transition-colors
            ${!isExpanded && 'justify-center'}
          `} title={!isExpanded ? 'Help' : undefined} data-id="element-1278">
          <HelpCircle size={20} data-id="element-1279" />
          {isExpanded && <span className="text-sm font-medium" data-id="element-1280">Help</span>}
        </button>
      </div>
    </div>;
}